/**
 * render.ts — HTML5 canvas renderer for the Reflex Bandit.
 *
 * Adapted from mob's renderer (DPR-aware resize, offscreen floor cache,
 * agent easing, coin/rock drawing copied verbatim). The domain-specific
 * changes for this regime:
 *
 *   - NO belief heat-map — perception is noiseless; the agent sees raw
 *     tokens. There is no belief to visualise.
 *   - The 5×5 PERCEPT WINDOW is highlighted around the agent — this is the
 *     regime's defining visual: the cells whose tokens the agent sees.
 *   - A small DRIFT-STATUS badge in the canvas corner (green ok / amber
 *     warning / red drift) — the EWMA detector's state.
 *   - The bump-flash is kept (red ring when the agent hits a discovered
 *     stone) and a grab-flash (green ring when it collects gold).
 *
 * Layers, back-to-front:
 *   1. Floor + grid lines.
 *   2. Percept-window tint (the 5×5 cells around the agent).
 *   3. Ground truth (gold coins + stone rocks).
 *   4. Agent disc + heading + grab/bump flash.
 *   5. Drift-status badge overlay.
 */

import { GRID, FREE, GOLD, STONE, HALF_W, WINDOW } from './constants';
import type { ReflexEnv } from './environment';
import type { DriftStatus } from './constants';

const FLOOR = '#0b1a24';
const GRID_LINE = 'rgba(255,255,255,0.04)';
const GOLD_COLOR = '#e0b34a';
const GOLD_HIGHLIGHT = '#f7dd92';
const GOLD_SHADOW = '#7a5512';
const GOLD_INNER = '#c08f1a';
const STONE_COLOR = '#4a5b6b';
const STONE_HIGHLIGHT = '#7c93a6';
const STONE_SHADOW = '#28323e';
const STONE_CRACK = '#1a232e';
const WINDOW_TINT = 'rgba(76, 114, 176, 0.10)';
const WINDOW_BORDER = 'rgba(76, 114, 176, 0.45)';
const AGENT_BODY = '#cde8ff';
const AGENT_RING = '#4c72b0';

export interface AgentAnim {
  r: number;
  c: number;
  sr: number;
  sc: number;
  heading: number;
  targetHeading: number;
  bumpFlash: number; // decays 1 → 0
  grabFlash: number; // decays 1 → 0
}

export function makeAgentAnim(r: number, c: number): AgentAnim {
  return { r, c, sr: r, sc: c, heading: 0, targetHeading: 0, bumpFlash: 0, grabFlash: 0 };
}

export interface RenderState {
  env: ReflexEnv;
  agent: AgentAnim;
  /** Current drift status for the badge overlay. */
  drift: DriftStatus;
  /** True to draw all cells at full opacity (regardless of window). */
  revealAll: boolean;
  /** True to highlight the percept window. */
  showWindow: boolean;
}

export class Renderer {
  private ctx: CanvasRenderingContext2D;
  private bg: HTMLCanvasElement;

  constructor(private canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('2D canvas not supported');
    this.ctx = ctx;
    this.bg = document.createElement('canvas');
    this.resize();
  }

  resize(): void {
    const dpr = window.devicePixelRatio || 1;
    const rect = this.canvas.getBoundingClientRect();
    const w = Math.floor(rect.width);
    const h = Math.floor(rect.height);
    if (w === 0 || h === 0) return;
    this.canvas.width = Math.floor(w * dpr);
    this.canvas.height = Math.floor(h * dpr);
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.renderBackground();
  }

  get widthPx(): number {
    return this.canvas.getBoundingClientRect().width;
  }
  get heightPx(): number {
    return this.canvas.getBoundingClientRect().height;
  }

  private cellPx(): number {
    return Math.min(this.widthPx / GRID, this.heightPx / GRID);
  }

  private renderBackground(): void {
    const w = this.widthPx;
    const h = this.heightPx;
    this.bg.width = Math.floor(w);
    this.bg.height = Math.floor(h);
    const ctx = this.bg.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = FLOOR;
    ctx.fillRect(0, 0, w, h);
  }

  frame(t: number, state: RenderState): void {
    const w = this.widthPx;
    const h = this.heightPx;
    if (w === 0 || h === 0 || this.bg.width === 0) {
      this.resize();
      if (this.bg.width === 0) return;
    }
    const ctx = this.ctx;
    const cell = this.cellPx();
    const ox = (w - cell * GRID) / 2;
    const oy = (h - cell * GRID) / 2;

    // Layer 1 — floor + grid.
    ctx.drawImage(this.bg, 0, 0, w, h);
    ctx.strokeStyle = GRID_LINE;
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (let i = 0; i <= GRID; i++) {
      ctx.moveTo(ox + i * cell, oy);
      ctx.lineTo(ox + i * cell, oy + GRID * cell);
      ctx.moveTo(ox, oy + i * cell);
      ctx.lineTo(ox + GRID * cell, oy + i * cell);
    }
    ctx.stroke();

    // Layer 2 — percept-window tint (the 5×5 cells around the agent).
    if (state.showWindow) {
      this.drawWindow(state, cell, ox, oy);
    }

    // Layer 3 — ground truth (gold coins + stone rocks).
    this.drawWorld(state, cell, ox, oy);

    // Layer 4 — agent.
    this.drawAgent(state, cell, ox, oy, t);

    // Layer 5 — drift-status badge (top-right corner).
    this.drawDriftBadge(state);
  }

  /** The 5×5 percept window tinted + outlined. */
  private drawWindow(state: RenderState, cell: number, ox: number, oy: number): void {
    const ctx = this.ctx;
    const { r, c } = state.env;
    const x0 = ox + (c - HALF_W) * cell;
    const y0 = oy + (r - HALF_W) * cell;
    const size = WINDOW * cell;
    ctx.fillStyle = WINDOW_TINT;
    ctx.fillRect(x0, y0, size, size);
    ctx.strokeStyle = WINDOW_BORDER;
    ctx.lineWidth = 1.5;
    ctx.strokeRect(x0, y0, size, size);
  }

  private drawWorld(state: RenderState, cell: number, ox: number, oy: number): void {
    const ctx = this.ctx;
    const truth = state.env.truth;
    if (!truth) return;
    const { r: ar, c: ac } = state.env;
    for (let r = 0; r < GRID; r++) {
      for (let c = 0; c < GRID; c++) {
        const kind = truth[r * GRID + c];
        if (kind === FREE) continue;
        const px = ox + c * cell;
        const py = oy + r * cell;
        if (kind === GOLD) {
          this.drawCoin(ctx, px, py, cell, r * GRID + c);
        } else if (kind === STONE) {
          this.drawRock(ctx, px, py, cell, r * GRID + c);
        }
      }
    }
    void ar;
    void ac;
    void state.revealAll; // tokens are always visible (noiseless regime)
  }

  /** Draw a coin (copied verbatim from mob's renderer). */
  private drawCoin(
    ctx: CanvasRenderingContext2D,
    px: number,
    py: number,
    cell: number,
    seed: number,
  ): void {
    const cx = px + cell * 0.5;
    const cy = py + cell * 0.5;
    const radius = cell * 0.34;
    const grad = ctx.createRadialGradient(
      cx - radius * 0.35,
      cy - radius * 0.35,
      radius * 0.1,
      cx,
      cy,
      radius,
    );
    grad.addColorStop(0, GOLD_HIGHLIGHT);
    grad.addColorStop(0.55, GOLD_COLOR);
    grad.addColorStop(1, GOLD_SHADOW);
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = GOLD_SHADOW;
    ctx.lineWidth = Math.max(0.6, cell * 0.04);
    ctx.stroke();
    ctx.strokeStyle = GOLD_INNER;
    ctx.lineWidth = Math.max(0.4, cell * 0.03);
    ctx.beginPath();
    ctx.arc(cx, cy, radius * 0.62, 0, Math.PI * 2);
    ctx.stroke();
    ctx.fillStyle = GOLD_HIGHLIGHT;
    ctx.beginPath();
    ctx.arc(cx - radius * 0.38, cy - radius * 0.38, radius * 0.14, 0, Math.PI * 2);
    ctx.fill();
    void seed;
  }

  /** Draw a rock (copied verbatim from mob's renderer). */
  private drawRock(
    ctx: CanvasRenderingContext2D,
    px: number,
    py: number,
    cell: number,
    seed: number,
  ): void {
    let s = (seed * 2654435761) >>> 0;
    const rand = () => {
      s = (s + 0x6d2b79f5) | 0;
      let t = Math.imul(s ^ (s >>> 15), 1 | s);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
    const nVerts = 7;
    const baseR = cell * 0.42;
    const cx = px + cell * 0.5;
    const cy = py + cell * 0.5;
    ctx.beginPath();
    for (let k = 0; k < nVerts; k++) {
      const ang = (k / nVerts) * Math.PI * 2;
      const rj = baseR * (0.8 + rand() * 0.4);
      const vx = cx + Math.cos(ang) * rj;
      const vy = cy + Math.sin(ang) * rj;
      if (k === 0) ctx.moveTo(vx, vy);
      else ctx.lineTo(vx, vy);
    }
    ctx.closePath();
    const grad = ctx.createLinearGradient(cx - baseR, cy - baseR, cx + baseR, cy + baseR);
    grad.addColorStop(0, STONE_HIGHLIGHT);
    grad.addColorStop(0.5, STONE_COLOR);
    grad.addColorStop(1, STONE_SHADOW);
    ctx.fillStyle = grad;
    ctx.fill();
    ctx.strokeStyle = STONE_SHADOW;
    ctx.lineWidth = Math.max(0.5, cell * 0.03);
    ctx.stroke();
    ctx.strokeStyle = STONE_CRACK;
    ctx.lineWidth = Math.max(0.4, cell * 0.025);
    for (let k = 0; k < 2; k++) {
      const ang = rand() * Math.PI * 2;
      const r0 = baseR * 0.2;
      const r1 = baseR * (0.55 + rand() * 0.2);
      ctx.beginPath();
      ctx.moveTo(cx + Math.cos(ang) * r0, cy + Math.sin(ang) * r0);
      ctx.lineTo(cx + Math.cos(ang + 0.2) * r1, cy + Math.sin(ang + 0.2) * r1);
      ctx.stroke();
    }
  }

  private drawAgent(
    state: RenderState,
    cell: number,
    ox: number,
    oy: number,
    t: number,
  ): void {
    const ctx = this.ctx;
    const a = state.agent;
    const ease = 0.25;
    a.sr += (a.r - a.sr) * ease;
    a.sc += (a.c - a.sc) * ease;
    let dh = a.targetHeading - a.heading;
    while (dh > Math.PI) dh -= 2 * Math.PI;
    while (dh < -Math.PI) dh += 2 * Math.PI;
    a.heading += dh * 0.3;
    if (a.bumpFlash > 0) a.bumpFlash = Math.max(0, a.bumpFlash - 0.04);
    if (a.grabFlash > 0) a.grabFlash = Math.max(0, a.grabFlash - 0.04);

    const px = ox + (a.sc + 0.5) * cell;
    const py = oy + (a.sr + 0.5) * cell;
    const radius = cell * 0.34;

    // Bump flash (red) — bumped a discovered stone.
    if (a.bumpFlash > 0) {
      ctx.strokeStyle = `rgba(196, 78, 82, ${a.bumpFlash})`;
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(px, py, radius * (1.4 + (1 - a.bumpFlash) * 0.8), 0, Math.PI * 2);
      ctx.stroke();
    }
    // Grab flash (green) — collected gold.
    if (a.grabFlash > 0) {
      ctx.strokeStyle = `rgba(85, 168, 104, ${a.grabFlash})`;
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(px, py, radius * (1.4 + (1 - a.grabFlash) * 0.8), 0, Math.PI * 2);
      ctx.stroke();
    }

    ctx.fillStyle = AGENT_BODY;
    ctx.strokeStyle = AGENT_RING;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(px, py, radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    ctx.strokeStyle = AGENT_RING;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(px, py);
    ctx.lineTo(px + Math.cos(a.heading) * radius, py + Math.sin(a.heading) * radius);
    ctx.stroke();
    void t;
  }

  /** Drift-status badge in the top-right corner. */
  private drawDriftBadge(state: RenderState): void {
    const ctx = this.ctx;
    const w = this.widthPx;
    const labels: Record<DriftStatus, { text: string; color: string }> = {
      warmup: { text: 'WARMUP', color: 'rgba(138,155,171,0.9)' },
      ok: { text: 'OK', color: 'rgba(85,168,104,0.9)' },
      warning: { text: 'WARNING', color: 'rgba(224,179,74,0.95)' },
      drift: { text: 'DRIFT', color: 'rgba(196,78,82,0.95)' },
    };
    const { text, color } = labels[state.drift];
    const padX = 8;
    const padY = 5;
    ctx.font = '10px ui-monospace, SF Mono, Menlo, monospace';
    const metrics = ctx.measureText(text);
    const boxW = metrics.width + padX * 2;
    const boxH = 18;
    const x = w - boxW - 12;
    const y = 12;
    ctx.fillStyle = 'rgba(11,26,36,0.85)';
    ctx.fillRect(x, y, boxW, boxH);
    ctx.strokeStyle = color;
    ctx.lineWidth = 1;
    ctx.strokeRect(x + 0.5, y + 0.5, boxW - 1, boxH - 1);
    ctx.fillStyle = color;
    ctx.fillText(text, x + padX, y + boxH - padY - 1);
  }
}
